import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useToast } from '../components/ToastProvider';
import { api } from '../services/api';
import type { UserDetail } from '../services/types';
import { copyText } from '../utils/copyText';
import { formatDate, formatMoney, subStatusClass, subStatusLabel } from '../utils/format';

type Tab = 'overview' | 'activity' | 'payments' | 'vpn' | 'promos' | 'invites' | 'support';

type ActivityItem = {
  id: string;
  kind: string;
  text: string;
  at: string;
};

function buildActivity(user: UserDetail): ActivityItem[] {
  const items: ActivityItem[] = [];

  for (const p of user.payments) {
    items.push({
      id: `pay-${p.id}`,
      kind: 'Платёж',
      text: `${formatMoney(p.amount)} · ${p.status} · ${p.period}${p.subscription_tier ? ` · ${p.subscription_tier}` : ''}`,
      at: p.created_at,
    });
  }
  for (const k of user.vpn_keys) {
    items.push({
      id: `vpn-${k.protocol ?? 'wg'}-${k.id}`,
      kind: (k.protocol ?? 'wg').toUpperCase(),
      text: `${k.server_country.toUpperCase()} · #${k.client_id} · ${k.status}`,
      at: k.created_at,
    });
  }
  for (const p of user.promos) {
    items.push({
      id: `promo-${p.code}-${p.used_at}`,
      kind: 'Промо',
      text: `Активирован код ${p.code}`,
      at: p.used_at,
    });
  }
  for (const i of user.invites_owned) {
    items.push({
      id: `inv-own-${i.code}-${i.used_by ?? 'x'}-${i.used_at ?? i.created_at}`,
      kind: 'Реферал',
      text: i.used_by
        ? `Друг ${i.used_by} активировал код ${i.code || user.referral_code || '—'}`
        : `Инвайт ${i.code || '—'} (${i.status})`,
      at: i.used_at ?? i.created_at,
    });
  }
  for (const i of user.invites_used) {
    items.push({
      id: `inv-used-${i.code}-${i.owner_id}-${i.used_at ?? i.created_at}`,
      kind: 'Инвайт',
      text: `Ввёл код ${i.code || '—'} от ${i.owner_id ?? '—'} (+${i.bonus_days} дн.)`,
      at: i.used_at ?? i.created_at,
    });
  }
  for (const t of user.support_tickets ?? []) {
    items.push({
      id: `sup-${t.id}`,
      kind: 'Поддержка',
      text: `Тикет #${t.ticket_number} · ${t.status}`,
      at: t.created_at,
    });
  }

  return items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const toast = useToast();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      const res = await api.getUser(Number(userId));
      if (res.data) setUser(res.data);
      else setError(res.error?.message ?? 'Пользователь не найден');
    })();
  }, [userId]);

  const activity = useMemo(() => (user ? buildActivity(user) : []), [user]);

  if (error) return <p style={{ color: 'var(--danger)' }}>{error}</p>;
  if (!user) return <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>;

  const tickets = user.support_tickets ?? [];

  const copyId = async () => {
    const ok = await copyText(String(user.user_id));
    if (ok) toast.success(`ID ${user.user_id} скопирован`);
    else toast.error('Не удалось скопировать ID');
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Обзор' },
    { id: 'activity', label: `Лента (${activity.length})` },
    { id: 'payments', label: `Платежи (${user.payments.length})` },
    { id: 'vpn', label: `VPN (${user.vpn_keys.length})` },
    { id: 'promos', label: `Промо (${user.promos.length})` },
    { id: 'invites', label: `Инвайты (${user.invites_owned.length + user.invites_used.length})` },
    { id: 'support', label: `Поддержка (${tickets.length})` },
  ];

  return (
    <div>
      <Link to="/users" style={{ fontSize: '0.875rem' }}>
        ← К списку
      </Link>
      <div className="page-header" style={{ marginTop: '0.5rem' }}>
        <h1>
          {user.first_name ?? 'User'}{' '}
          <button type="button" className="user-drawer-id-btn" onClick={() => void copyId()} title="Скопировать ID">
            #{user.user_id}
          </button>
          {user.username ? ` @${user.username}` : ''}
        </h1>
        <button type="button" className="btn btn-ghost" onClick={() => void copyId()}>
          Скопировать ID
        </button>
      </div>

      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid-stats">
          <InfoCard label="Подписка">
            <span className={subStatusClass(user.subscription_status)}>
              {subStatusLabel(user.subscription_status)}
            </span>
          </InfoCard>
          <InfoCard label="Tier">{user.subscription_tier ?? '—'}</InfoCard>
          <InfoCard label="До">{formatDate(user.subscription_until)}</InfoCard>
          <InfoCard label="VPN ключей">{user.active_keys_count}</InfoCard>
          <InfoCard label="Язык">{user.language}</InfoCard>
          <InfoCard label="Telegram Premium">{user.is_premium ? 'Да' : 'Нет'}</InfoCard>
          <InfoCard label="Уведомления об истечении">
            {user.notify_expiry ? 'Включены' : 'Выключены'}
          </InfoCard>
          <InfoCard label="Реферальный код">{user.referral_code ?? '—'}</InfoCard>
          <InfoCard label="Платежей">{user.payments.length}</InfoCard>
          <InfoCard label="Промо / инвайты">
            {user.promos.length} / {user.invites_used.length}
          </InfoCard>
          <InfoCard label="Тикетов поддержки">{tickets.length}</InfoCard>
        </div>
      )}

      {tab === 'activity' && (
        <div className="activity-list">
          {activity.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>Пока нет событий</p>
          ) : (
            activity.map((item) => (
              <div key={item.id} className="activity-item">
                <span className="activity-kind">{item.kind}</span>
                <div className="activity-body">{item.text}</div>
                <div className="activity-time">{formatDate(item.at)}</div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'payments' && (
        <Table
          headers={['ID', 'Период', 'Tier', 'Дней', 'Сумма', 'Статус', 'Дата']}
          rows={user.payments.map((p) => [
            <Link key={p.id} to={`/payments/${encodeURIComponent(p.id)}`}>
              {p.id.slice(0, 18)}…
            </Link>,
            p.period,
            p.subscription_tier ?? '—',
            p.days,
            formatMoney(p.amount),
            p.status,
            formatDate(p.created_at),
          ])}
          empty="Нет платежей"
        />
      )}

      {tab === 'vpn' && (
        <Table
          headers={['Протокол', 'ID', 'Сервер', 'Client ID', 'Статус', 'Создан']}
          rows={user.vpn_keys.map((k) => [
            (k.protocol ?? 'wg').toUpperCase(),
            k.protocol === 'ovpn' ? (
              k.id
            ) : (
              <Link key={`id-${k.id}`} to={`/vpn/${k.id}`}>
                {k.id}
              </Link>
            ),
            k.server_country.toUpperCase(),
            k.client_id,
            k.status,
            formatDate(k.created_at),
          ])}
          empty="Нет ключей"
        />
      )}

      {tab === 'promos' && (
        <Table
          headers={['Код', 'Использован']}
          rows={user.promos.map((p) => [p.code, formatDate(p.used_at)])}
          empty="Промокоды не использовались"
        />
      )}

      {tab === 'invites' && (
        <>
          {user.referral_code && (
            <p style={{ marginTop: 0 }}>
              Реферальный код: <code>{user.referral_code}</code>
              {user.invites_owned.length > 0
                ? ` · друзей: ${user.invites_owned.filter((i) => i.status === 'used').length}`
                : ''}
            </p>
          )}
          <h3>Друзья по коду (активировали его реферал)</h3>
          <Table
            headers={['Код', 'Бонус другу', 'Tier друга', 'Статус', 'Кем использован', 'Дата']}
            rows={user.invites_owned.map((i) => [
              i.code || '—',
              `+${i.bonus_days} дн.`,
              i.subscription_tier,
              i.status,
              i.used_by ? (
                <Link key={`u-${i.used_by}`} to={`/users/${i.used_by}`}>
                  {i.used_by}
                </Link>
              ) : (
                '—'
              ),
              formatDate(i.used_at ?? i.created_at),
            ])}
            empty="Никто ещё не активировал код"
          />
          <h3>Активированные инвайты (этот пользователь ввёл код)</h3>
          <Table
            headers={['Код', 'От кого', 'Получено', 'Tier', 'Использован']}
            rows={user.invites_used.map((i) => [
              i.code || '—',
              i.owner_id ? (
                <Link key={`o-${i.owner_id}`} to={`/users/${i.owner_id}`}>
                  {i.owner_id}
                </Link>
              ) : (
                '—'
              ),
              `+${i.bonus_days} дн.`,
              i.subscription_tier,
              formatDate(i.used_at),
            ])}
            empty="Инвайты не активировал"
          />
        </>
      )}

      {tab === 'support' && (
        <Table
          headers={['#', 'Статус', 'Message ID', 'Создан']}
          rows={tickets.map((t) => [
            t.ticket_number,
            t.status,
            t.message_id ?? '—',
            formatDate(t.created_at),
          ])}
          empty="Обращений в поддержку нет (или таблица support_tickets отсутствует)"
        />
      )}
    </div>
  );
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function Table({
  headers,
  rows,
  empty,
}: {
  headers: string[];
  rows: React.ReactNode[][];
  empty: string;
}) {
  if (rows.length === 0) {
    return <p style={{ color: 'var(--text-muted)' }}>{empty}</p>;
  }
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
